/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import {
  decryptAES,
  maskEmailID,
  maskPhoneNumber,
  passkeyOtpGenerateRequest,
  passkeyPreferencesRequest,
  resetPasskeyOtpGenerate,
  RootState,
  useRecaptchaToken,
} from '@rb-serve-app/core';
import Spinner from '../../components/Spinner';
import InlineError from '../../components/ErrorComponents/InlineError';
import SuccessModel from '../../components/SuccessModel';

interface PasskeyVerifyIdentityModalProps {
  userName: string;
  passkeyToken: string;
  onClose: () => void;
  onSendCodeSuccess: (selectedContact: string, preferenceType: string) => void;
}

const DESIRED_ORDER = ['EMAIL', 'ALTERNATE_1', 'HOME', 'BUSINESS', 'ALTERNATE_2'];

const decryptValue = (value?: string): string => {
  if (!value) {
    return '';
  }
  try {
    return decryptAES(value) || value;
  } catch {
    return value;
  }
};

const PasskeyVerifyIdentityModal = ({
  userName,
  passkeyToken,
  onClose,
  onSendCodeSuccess,
}: PasskeyVerifyIdentityModalProps) => {
  const dispatch = useDispatch();
  const getRecaptchaToken = useRecaptchaToken();
  const modalRef = useRef<HTMLDivElement>(null);
  const { handleSubmit, control } = useForm({
    defaultValues: { contactMethod: '' },
  });

  const [selectedMethod, setSelectedMethod] = useState('');
  const [preferenceType, setPreferenceType] = useState<string | null>(null);
  const [error, setError] = useState<{ title: string; description: string } | null>(
    null
  );
  const [sortedData, setSortedData] = useState<any[]>([]);

  const { contentfulData } = useSelector(
    (state: RootState) => state.contentful
  );
  const { entries } = contentfulData || {};
  const otpContentFullData = entries?.rb_otp_flow;
  const prefMethods = otpContentFullData?.blockedUserPopupContent?.verification_method;
  const { loading, preferences, error: preferencesError } = useSelector(
    (state: RootState) => state.passkeyOtpPreferences
  );
  const { loading: isGenerateCodeLoading, generated, error: generateError } =
    useSelector((state: RootState) => state.passkeyOtpGenerate);

  useEffect(() => {
    if (userName && passkeyToken) {
      dispatch(passkeyPreferencesRequest({ username: userName, passkeyToken }));
    }
  }, [dispatch, userName, passkeyToken]);

  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (preferences?.length) {
      const sorted = [...preferences].sort(
        (a: any, b: any) =>
          DESIRED_ORDER.indexOf(a.type) - DESIRED_ORDER.indexOf(b.type)
      );
      setSortedData(sorted);
    }
  }, [preferences]);

  useEffect(() => {
    if (generated && selectedMethod && preferenceType) {
      onSendCodeSuccess(selectedMethod, preferenceType);
      dispatch(resetPasskeyOtpGenerate());
    }
  }, [
    generated,
    selectedMethod,
    preferenceType,
    onSendCodeSuccess,
    dispatch,
  ]);

  useEffect(() => {
    if (!generateError) {
      return;
    }
    if (generateError?.status == '401' || generateError?.statusCode == '401') {
      setError({
        title:
          entries?.rb_error?.recaptchaErrors[generateError?.errorCode]?.title,
        description:
          entries?.rb_error?.recaptchaErrors[generateError?.errorCode]
            ?.description,
      });
    } else {
      setError({
        title: entries?.rb_error?.genericApiFailure?.text,
        description: entries?.rb_error?.genericApiFailure?.text,
      });
    }
  }, [generateError, entries]);

  const handleMethodChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    item: any
  ) => {
    setSelectedMethod(e.target.value);
    setPreferenceType(item?.type);
  };

  const sendCode = async () => {
    if (!preferenceType) {
      return;
    }
    const recaptchaToken = await getRecaptchaToken('passkey_generate_otp');
    dispatch(
      passkeyOtpGenerateRequest({
        preferenceType,
        username: userName,
        passkeyToken,
        recaptchaToken,
      })
    );
  };

  const isDisabled = !selectedMethod;

  if (preferencesError) {
    return (
      <SuccessModel
        isOpen={true}
        title={entries?.rb_error?.genericApiFailure?.header}
        subTitle={entries?.rb_error?.genericApiFailure?.text}
        successIcon={false}
        closeModal={onClose}
        errorIcon={false}
      />
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 min-h-screen">
      {loading || !otpContentFullData ? (
        <Spinner />
      ) : (
        <>
          <div className="flex items-center justify-center md:p-4 p-0 md:max-h-[80vh] md:h-[80vh] max-h-full h-full w-full md:w-[60%]">
            <div
              className="xl:w-[100%] lg:w-[100%] md:w-[100%] sm:w-full md:max-h-[70vh] md:h-[70vh] max-h-full h-full overflow-y-auto bg-white md:rounded-[15px] rounded-none shadow-lg"
              ref={modalRef}
              tabIndex={-1}
              role="dialog"
              aria-labelledby="passkey-otp-title"
              aria-describedby="passkey-otp-description"
            >
              <div className="px-[30px] py-11 flex flex-col justify-center items-center">
                <h2
                  id="passkey-otp-title"
                  className="text-left md:text-center xl:text-[28px] text-[16px] md:font-[400] font-[600] md:leading-[34px] leading-none"
                >
                  {otpContentFullData?.idSelectContactMethod}
                </h2>
                <p
                  id="passkey-otp-description"
                  className="text-left md:text-center xl:text-[20px] text-[15px] font-normal md:leading-[25px] leading-tight mt-[10px]"
                >
                  {otpContentFullData?.idAccountLockedTitle}
                </p>
                {error && (
                  <div className="mt-5 w-full">
                    <InlineError
                      errorText={error?.description}
                      aria-live="assertive"
                      fontSize={'text-[15px]'}
                      onClose={() => setError(null)}
                      oncloseHide={true}
                    />
                  </div>
                )}
                <div className="model-body flex flex-col xl:w-[68%] lg:w-full justify-center items-center">
                  <form
                    onSubmit={handleSubmit(sendCode)}
                    className="space-y-4 mt-6 w-full"
                    aria-labelledby="passkey-contact-methods"
                  >
                    <div
                      id="passkey-contact-methods"
                      role="radiogroup"
                      aria-labelledby="passkey-otp-title"
                      className="flex justify-start items-start flex-col"
                    >
                      {sortedData.map((item: any) => (
                        <div
                          key={item?.value}
                          className="flex items-center justify-start gap-[10px] px-5 py-4 self-stretch border border-[#E9E9E9] bg-white"
                        >
                          <Controller
                            name="contactMethod"
                            control={control}
                            render={({ field }) => (
                              <input
                                {...field}
                                type="radio"
                                id={`passkey-${item?.value}`}
                                value={item?.value}
                                onChange={(e) => handleMethodChange(e, item)}
                                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 accent-[#DA291C]"
                              />
                            )}
                          />
                          <div
                            className={`${item?.type !== 'EMAIL' && 'flex flex-col gap-0'}`}
                          >
                            {item?.type !== 'EMAIL' && (
                              <span className="ml-2 text-[12px] font-[400] text-gray-800">
                                {prefMethods?.[item?.type?.toLowerCase()]}
                              </span>
                            )}
                            <label
                              htmlFor={`passkey-${item?.value}`}
                              className="ml-2 text-[16px] font-[400] text-gray-800"
                            >
                              {item?.type === 'EMAIL'
                                ? maskEmailID(decryptValue(item?.value))
                                : maskPhoneNumber(decryptValue(item?.value))}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className="flex justify-center items-center gap-[20px] mt-[30px] md:relative md:bottom-0 fixed bottom-[30px] md:z-0 z-[999] right-[30px] left-[30px] md:left-0 md:right-0"
                      style={{ marginTop: '30px' }}
                    >
                      <button
                        aria-label={
                          otpContentFullData?.deviceNotRecogonizedPopupCtaSecondaryTitle
                        }
                        type="button"
                        onClick={onClose}
                        className="p-[12px] text-text16 font-[600] bg-white hover:bg-red-50 text-primary-red focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 border border-primary-red w-[160px] h-[48px]"
                      >
                        {
                          otpContentFullData?.deviceNotRecogonizedPopupCtaSecondaryTitle
                        }
                      </button>
                      <div className="relative w-[160px]">
                        <button
                          aria-label={
                            otpContentFullData?.identityVerificationContactMethodCtaPrimary
                          }
                          type="submit"
                          disabled={isDisabled || isGenerateCodeLoading}
                          className={`${isDisabled && !isGenerateCodeLoading ? 'bg-disabled-bg-color text-Text-disabled-btn border-[#D9D9D9] border min-h-[48px]' : 'bg-[#DA291C] hover:bg-red-700 text-white focus:ring-red-500'}  w-[160px] h-[48px] font-medium py-2 px-4 focus:outline-none focus:ring-2 focus:ring-opacity-50`}
                        >
                          {isGenerateCodeLoading
                            ? ''
                            : otpContentFullData?.identityVerificationContactMethodCtaPrimary}
                        </button>
                        {isGenerateCodeLoading && (
                          <div className="flex justify-center items-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                            <div className="animate-spin ease-linear rounded-full border-4 border-t-4 border-white h-6 w-6"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
          <div className="block md:hidden fixed bottom-0 left-0 w-full h-[12%] bg-[#FAFAFA] border-t border-[#E9E9E9]"></div>
        </>
      )}
    </div>
  );
};

export default PasskeyVerifyIdentityModal;
